import Meeting from "../meeting/meeting";
import DeviceUtility from "../helpers/device";
import { IMediaClient } from "../interfaces/IMediaClient";
import { MeetingSettings } from "../types/MeetingSettings";
import { ref } from "vue";
import ZoomVideo from "@zoom/videosdk";

export enum MediaAccessValidationMissingFeature {
	AudioContext = "audioContext",
	MediaDevices = "mediaDevices",
	VoIP = "voip",
	Video = "video",
	PermissionsQuery = "permissionsQuery"
}

class MediaAccessValidationResult {
	deviceInUse: boolean;
	permissionRequired: boolean;
	userInteractionRequired: boolean | null;
	userMedia: MediaStream | null;
	audioContext: AudioContext | null;
	modernBrowserRequired: boolean | null;
	missingFeature: MediaAccessValidationMissingFeature | null;
	deviceInUseName: "camera" | "microphone";

	constructor(
		deviceInUse: boolean,
		permissionRequired: boolean,
		userInteractionRequired: boolean | null,
		userMedia: MediaStream | null,
		audioContext: AudioContext | null,
		modernBrowserRequired: boolean | null
	) {
		this.deviceInUse = deviceInUse;
		this.permissionRequired = permissionRequired;
		this.userInteractionRequired = userInteractionRequired;
		this.userMedia = userMedia;
		this.audioContext = audioContext;
		this.modernBrowserRequired = modernBrowserRequired;
		this.missingFeature = null;
		this.deviceInUseName = "microphone"; // default to microphone, that's the most common case
	}
}

const _mediaClient = ref(null as IMediaClient | null);
const _meetingSettings = ref(null as MeetingSettings | null);
const _mediaAccessValidationResult = ref(null as MediaAccessValidationResult | null);

export const useMeetingConfiguration = () => {
	const getMediaClient = () => {
		if (_mediaClient.value) {
			return _mediaClient.value;
		} else {
			return undefined;
		}
	};
	const setMediaClient = (mc: IMediaClient) => {
		(window as any).mediaClient = mc; // for debugging purposes
		_mediaClient.value = mc;
	};

	const getPingUrl = () => {
		if (_meetingSettings.value) {
			return _meetingSettings.value.pingUrl;
		} else {
			throw new Error("Meeting settings are not specified. Use setSettings to set them.");
		}
	};

	const getSettings = () => {
		if (_meetingSettings.value) {
			return _meetingSettings.value;
		} else {
			throw new Error("Meeting settings are not specified. Use setSettings to set them.");
		}
	};

	const setSettings = (settings: MeetingSettings) => {
		if (settings instanceof MeetingSettings) {
			_meetingSettings.value = settings;
		} else {
			throw new Error("Invalid settings. It must be an instance of MeetingSettings.");
		}
	};

	const hasAudioAccess = (userMedia: MediaStream) => {
		const device = Meeting.detectMobileDevice();
		const audioTracks = userMedia.getAudioTracks();

		if (!audioTracks || audioTracks.length === 0) {
			console.log("audio access check failed - audio track is not present");
			return false;
		}
		// On mobile/tablet, also ensure the first audio track is not muted
		if (device.isMobileDevice && audioTracks[0].muted) {
			console.log("audio access check failed - audio track is muted");
			return false;
		}
		return true;
	};

	const validateMediaAccess = async (): Promise<MediaAccessValidationResult> => {
		// attempt the media access; this allows a single shot access to the camera and microphone
		// if we fail, we will show the permissions screen
		const settings = getSettings();

		if (!(window as any).AudioContext) {
			console.log("media access validation - no audio context");
			_mediaAccessValidationResult.value = new MediaAccessValidationResult(false, false, null, null, null, true);
			_mediaAccessValidationResult.value.missingFeature = MediaAccessValidationMissingFeature.AudioContext;
			return _mediaAccessValidationResult.value;
		}

		if (!navigator.mediaDevices) {
			console.log("media access validation - no media devices");
			_mediaAccessValidationResult.value = new MediaAccessValidationResult(false, false, null, null, null, true);
			_mediaAccessValidationResult.value.missingFeature = MediaAccessValidationMissingFeature.MediaDevices;
			return _mediaAccessValidationResult.value;
		}

		try {
			await navigator.permissions.query({ name: "camera" as PermissionName });
			await navigator.permissions.query({ name: "microphone" as PermissionName });
		} catch (e) {
			console.log("media access validation - permissions query failed");
			_mediaAccessValidationResult.value = new MediaAccessValidationResult(false, false, null, null, null, true);
			_mediaAccessValidationResult.value.missingFeature = MediaAccessValidationMissingFeature.PermissionsQuery;
			return _mediaAccessValidationResult.value;
		}

		const compatibility = ZoomVideo.checkSystemRequirements();
		if (!compatibility.audio) {
			console.log("media access validation - audio compatibility check failed");
			_mediaAccessValidationResult.value = new MediaAccessValidationResult(false, false, null, null, null, true);
			_mediaAccessValidationResult.value.missingFeature = MediaAccessValidationMissingFeature.VoIP;
			return _mediaAccessValidationResult.value;
		}

		if (settings.isCameraRequired && !compatibility.video) {
			console.log("media access validation - video compatibility check failed and camera is required");
			_mediaAccessValidationResult.value = new MediaAccessValidationResult(false, false, null, null, null, true);
			_mediaAccessValidationResult.value.missingFeature = MediaAccessValidationMissingFeature.Video;
			return _mediaAccessValidationResult.value;
		}

		let userMedia: MediaStream | null = null;
		try {
			userMedia = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
			if (!hasAudioAccess(userMedia)) {
				console.log("media access validation - audio access granted, but audio track is either not present or muted");
				_mediaAccessValidationResult.value = new MediaAccessValidationResult(true, false, null, null, null, null);
				return _mediaAccessValidationResult.value;
			}
		} catch (e) {
			console.warn("error getting user media", e);
			if (settings.isCameraRequired) {
				if ((e as any).name == "NotReadableError") {
					// if the camera is required and we fail to read it, it is being used by another app
					console.log("media access validation - camera access denied, camera is being used by another app");
					_mediaAccessValidationResult.value = new MediaAccessValidationResult(true, true, null, null, null, null);
					_mediaAccessValidationResult.value.deviceInUseName = "camera";
					return _mediaAccessValidationResult.value;
				}
				// if the camera is required and we fail to access it, we need to show the permissions screen
				console.log("media access validation - camera access denied");
				_mediaAccessValidationResult.value = new MediaAccessValidationResult(false, true, null, null, null, null);
				return _mediaAccessValidationResult.value;
			} else {
				// if the camera is not required, we can try to access the microphone only
				// this is to allow the user to join the call without a camera
				try {
					userMedia = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
					if (!hasAudioAccess(userMedia)) {
						console.log(
							"media access validation - audio access granted when only microphone was requested, but audio track is either not present or muted"
						);
						_mediaAccessValidationResult.value = new MediaAccessValidationResult(true, false, null, null, null, null);
						return _mediaAccessValidationResult.value;
					}
				} catch (e) {
					console.warn("error getting user media (only microphone requested)", e);
					if ((e as any).name == "NotReadableError") {
						// if we fail to read the microphone, it is being used by another app
						console.log("media access validation - microphone access denied, microphone is being used by another app");
						_mediaAccessValidationResult.value = new MediaAccessValidationResult(true, true, null, null, null, null);
						return _mediaAccessValidationResult.value;
					}
					_mediaAccessValidationResult.value = new MediaAccessValidationResult(false, true, null, null, null, null);
					return _mediaAccessValidationResult.value;
				}
			}
		}

		// if the audio context is suspended, we need to show the join panel
		// this is an issue in Safari on Mac specifically, nowhere else
		const audioContext = new AudioContext();
		try {
			await audioContext.resume();
		} catch (e) {
			console.warn("media access validation - error resuming audio context", e);
			// if we can't resume at all, it's because the mic is in use by another app
			_mediaAccessValidationResult.value = new MediaAccessValidationResult(true, false, false, userMedia, audioContext, null);
			return _mediaAccessValidationResult.value;
		}
		const device = Meeting.detectMobileDevice();
		// if the audio context is suspended, we need to show the join panel
		// this is an issue in Safari on Mac specifically, nowhere else,
		// because it plays the context but still fails to play a moment later, soooo we ignore that
		// case. The extra check on the device is for iPad Pro, which is a desktop like mobile device
		if (audioContext.state == "suspended" || (DeviceUtility.isMac && DeviceUtility.isSafari && !device.isDesktopLikeMobileDevice)) {
			console.log("media access validation - audio context is suspended");
			_mediaAccessValidationResult.value = new MediaAccessValidationResult(false, false, true, userMedia, audioContext, null);
			return _mediaAccessValidationResult.value;
		}
		console.log("media access validation - audio context is not suspended, we are good to go");
		_mediaAccessValidationResult.value = new MediaAccessValidationResult(false, false, false, userMedia, audioContext, null);
		return _mediaAccessValidationResult.value;
	};

	const clearMediaAccessValidationResult = (audio: boolean = true, video: boolean = true) => {
		if (audio) {
			_mediaAccessValidationResult.value?.userMedia?.getAudioTracks().forEach((track) => track.stop());
		}
		if (video) {
			_mediaAccessValidationResult.value?.userMedia?.getVideoTracks().forEach((track) => track.stop());
		}
		_mediaAccessValidationResult.value = null;
	};

	const getBrowserCompatibilityError = () => {
		// Only support Chrome, Safari, and Edge - Edge is only supported on non iOS devices.

		if (DeviceUtility.isIOS || DeviceUtility.isIpad) {
			if (!DeviceUtility.isChrome && !DeviceUtility.isSafari) {
				return "Please use Chrome or Safari to join the meeting.";
			}
		}

		if (!DeviceUtility.isChrome && !DeviceUtility.isEdge && !DeviceUtility.isSafari) {
			if (DeviceUtility.isMac) {
				return "Please use Chrome, Edge, or Safari to join the meeting.";
			} else {
				return "Please use Chrome or Edge to join the meeting.";
			}
		}

		return "";
	};

	return {
		getMediaClient,
		setMediaClient,
		getPingUrl,
		getSettings,
		setSettings,
		validateMediaAccess,
		clearMediaAccessValidationResult,
		getBrowserCompatibilityError
	};
};
export default useMeetingConfiguration;
